import * as React from 'react'
import { PreloadedLink } from '../../preloaded-link'
import { Tooltip } from 'primitives/tooltip'
import { ROUTES } from '@repo/studio-utils/routes'
import { filterDesktopTools } from '@repo/studio-utils/desktop/tool-allowlist'
import { isStudioDesktop } from '@repo/studio-desktop-bridge/runtime'
// oxlint-disable-next-line cf-web/banned-imports
import { DotsVerticalIcon } from 'primitives/icons'
import { TOOL_ICONS } from '../../../config/tool-icons'
import { useStudioConfig, useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useStudioPathname } from '@repo/studio-config/studio-config-router'
import { tw } from 'utils/classnames'
import { LFS_URL } from '@repo/studio-utils/config/env'
import * as Popover from 'primitives/popover'
import { Scroller } from '../../scroller'
import { PopoverComponentsScrollable } from '../../popover-components'
import { Button } from '@repo/studio-design-system-ui-button'
import throttle from 'lodash.throttle'
import { useFeatureIsOn } from 'growthbook'
import { distanceToRect } from 'utils/dom'
import { useWindowDomEvent } from 'hooks/use-dom-event'
import { ToolType } from '@repo/rpc-types-creativefabrica-studiomediamatrix-v1'
import type { StudioWorkspaceId } from '@repo/studio-config/studio-config-context'
import type { ToolDefinition } from '../../../config/tool-definitions'
import { TOOL_DEFINITIONS } from '../../../config/tool-definitions'
import { TOOL_FEATURE_FLAG_YOUTUBE_THUMBNAIL_GENERATOR } from '@repo/studio-tool-config/tool-feature-flags'

const LFS_IMAGES = `${LFS_URL}/studio/images/tools_v3`

export const APP_SWITCH_CLUSTERS = [
  'audio',
  'video',
  'marketing',
  'utilities',
  'entertainment',
  '3d',
  'image',
  'crafting',
  'workspaces',
  'pinned',
] as const

export type AppSwitchCluster = (typeof APP_SWITCH_CLUSTERS)[number]
export type AppSwitchCategory = 'ai' | 'more'

export interface AppSwitchBarItem {
  toolType: ToolType
  title: string
  description: {
    default: string
    mobile: string
  }
  cta: string
  short_cta: string
  ariaLabel: string

  image: React.JSX.Element
  icon: React.JSX.Element

  linkClass?: string
  colorClass: string
  hoverColorClass: string
  focusColorClass: string
  className?: string

  active: boolean
  route: string
  showInEllipsisMenu?: boolean
  showOnHomePage?: boolean
  preload: boolean

  category: AppSwitchCategory
  order: number
  cluster?: AppSwitchCluster
  badge?: string
  hardNavigation?: boolean
  hideFavorite?: boolean
  /** Offered only inside these workspaces. Omitted means every Studio surface lists it. */
  workspaceIds?: readonly StudioWorkspaceId[]
}

// This is basically 'ToolTypeJson' but we can't just do 'Exclude', as we'd get a type error when a new app is added
export type ToolTypeTranslationKey =
  | 'TOOL_TYPE_UPSCALER'
  | 'TOOL_TYPE_QUOTE_GENERATOR'
  | 'TOOL_TYPE_DESIGN_REVIEWER'
  | 'TOOL_TYPE_IMAGE_EDITOR'
  | 'TOOL_TYPE_VIDEO_EDITOR'
  | 'TOOL_TYPE_AI_MUSIC_GENERATOR'
  | 'TOOL_TYPE_AI_WATERMARK_REMOVER'
  | 'TOOL_TYPE_SOUND_GENERATOR'
  | 'TOOL_TYPE_SPEECH_GENERATOR'
  | 'TOOL_TYPE_COLLAGE_MAKER'
  | 'TOOL_TYPE_FILE_CONVERTER'
  | 'TOOL_TYPE_STUDIO_DESIGNER'
  | 'TOOL_TYPE_VECTORIZER'
  | 'TOOL_TYPE_BG_REMOVER'
  | 'TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND'
  | 'TOOL_TYPE_FLOW'
  | 'TOOL_TYPE_UNLIMITED_IMAGE_GENERATOR'
  | 'TOOL_TYPE_LOGO_GENERATOR'
  | 'TOOL_TYPE_FONT_GENERATOR'
  | 'TOOL_TYPE_IMAGE_COMPRESSOR'
  | 'TOOL_TYPE_TRANSFERS'
  | 'TOOL_TYPE_THREED_MODEL_VIEWER'
  | 'TOOL_TYPE_IMAGE_RESIZER'
  | 'TOOL_TYPE_AI_PHOTO_STYLES'
  | 'TOOL_TYPE_AI_MOCKUP_GENERATOR'
  | 'TOOL_TYPE_THREED_PRINT_GENERATOR'
  | 'TOOL_TYPE_DOODLE_PET_GENERATOR'
  | 'TOOL_TYPE_COLOR_PALETTE_PICKER'
  | 'TOOL_TYPE_AI_COLORING_PAGE_MAKER'
  | 'TOOL_TYPE_VIDEO_GENERATOR'
  | 'TOOL_TYPE_VIDEO_DRAFT'
  | 'TOOL_TYPE_VIDEO_UNCENSORED'
  | 'TOOL_TYPE_WEDDING_INVITE_GENERATOR'
  | 'TOOL_TYPE_ETSY_COACH'
  | 'TOOL_TYPE_MESH_GENERATOR'
  | 'TOOL_TYPE_TOON_FAMILY_BUILDER'
  | 'TOOL_TYPE_THREED_SCULPT'
  | 'TOOL_TYPE_SPLAT_GENERATOR'
  | 'TOOL_TYPE_VIDEO_UPSCALER'
  | 'TOOL_TYPE_CAMERA_ANGLE'
  | 'TOOL_TYPE_AISVG_GENERATOR'
  | 'TOOL_TYPE_LUMO'
  | 'TOOL_TYPE_WORKFLOWS'
  | 'TOOL_TYPE_AD_CAMPAIGN_GENERATOR'
  | 'TOOL_TYPE_SUBTITLE_GENERATOR'
  | 'TOOL_TYPE_PRODUCT_MANAGER'
  | 'TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR'
  | 'TOOL_TYPE_IMAGE_ALIVE'
  | 'TOOL_TYPE_IMAGE_LAYERIZER'
  | 'TOOL_TYPE_VIDEO_EDITING_TOOLBOX'

// Pre-compute lookup map at module level (static data, never changes)
const toolDefMap = new Map<string, ToolDefinition>(TOOL_DEFINITIONS.map(d => [d.translationKey, d]))

function speechGeneratorRoute(
  activeWorkspaceId: StudioWorkspaceId | undefined,
  studioRoute: string | undefined,
) {
  if (activeWorkspaceId === 'unlimited') {
    return ROUTES.UNLIMITED.SPEECH_GENERATOR
  }
  return studioRoute ?? ROUTES.AI_SPEECH_GENERATOR.ROOT
}

function isOfferedInWorkspace(
  item: AppSwitchBarItem,
  activeWorkspaceId: StudioWorkspaceId | undefined,
): boolean {
  if (!item.workspaceIds) {
    return true
  }
  return activeWorkspaceId !== undefined && item.workspaceIds.includes(activeWorkspaceId)
}

export function useAppSwitchItems(): AppSwitchBarItem[] {
  const t = useStudioTranslate()
  const isNsfwPlatform = useStudioConfig()?.isNsfwPlatform
  const activeWorkspaceId = useStudioConfig()?.workspaceId
  const isYoutubeThumbnailGeneratorEnabled = useFeatureIsOn(
    toolDefMap.get('TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR')?.featureFlag ??
      TOOL_FEATURE_FLAG_YOUTUBE_THUMBNAIL_GENERATOR,
  )

  const getToolTranslations = (tool: ToolTypeTranslationKey) => ({
    title: t(`studio.tools.${tool}.title`) as string,
    description: {
      default: t(`studio.tools.${tool}.description.default`) as string,
      mobile: t(`studio.tools.${tool}.description.mobile`) as string,
    },
    cta: t(`studio.tools.${tool}.cta`) as string,
    short_cta: t(`studio.tools.${tool}.short_cta`) as string,
  })

  const aiImageEditor: AppSwitchBarItem = {
    toolType: ToolType.IMAGE_EDITOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_IMAGE_EDITOR')?.cluster ?? 'image',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-image-editor.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_IMAGE_EDITOR,
    ...getColorClasses('bg-studio-tools-logocreator'),
    ariaLabel: 'Switch to AI Image Editor',
    route: toolDefMap.get('TOOL_TYPE_IMAGE_EDITOR')?.route ?? ROUTES.AI_IMAGE_EDITOR.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_IMAGE_EDITOR'),
    order: toolDefMap.get('TOOL_TYPE_IMAGE_EDITOR')?.order ?? 0,
  }

  const flow: AppSwitchBarItem = {
    toolType: ToolType.FLOW,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_FLOW')?.cluster ?? 'image',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/flow.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_FLOW,
    ...getColorClasses('bg-studio-tools-flow'),
    linkClass: '[&>*]:h-[18px] [&>*]:min-w-[24px]',
    ariaLabel: 'Switch to Flow',
    route: toolDefMap.get('TOOL_TYPE_FLOW')?.route ?? ROUTES.FLOW.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_FLOW'),
    order: toolDefMap.get('TOOL_TYPE_FLOW')?.order ?? 1,
  }

  const unlimitedImageGenerator: AppSwitchBarItem = {
    toolType: ToolType.UNLIMITED_IMAGE_GENERATOR,
    category: 'ai',
    cluster: 'image',
    workspaceIds: ['unlimited'],
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/flow.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_UNLIMITED_IMAGE_GENERATOR,
    ...getColorClasses('bg-studio-tools-flow'),
    linkClass: '[&>*]:h-[18px] [&>*]:min-w-[24px]',
    ariaLabel: 'Switch to the Unlimited AI Image Generator',
    route: ROUTES.UNLIMITED.IMAGE_GENERATOR,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_UNLIMITED_IMAGE_GENERATOR'),
    order: 1.1,
  }

  const imageLayerizer: AppSwitchBarItem = {
    toolType: ToolType.IMAGE_LAYERIZER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_IMAGE_LAYERIZER')?.cluster ?? 'image',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/image-layerizer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_IMAGE_LAYERIZER,
    ariaLabel: 'Switch to Image Layerizer',
    route: toolDefMap.get('TOOL_TYPE_IMAGE_LAYERIZER')?.route ?? ROUTES.IMAGE_LAYERIZER.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_IMAGE_LAYERIZER'),
    order: toolDefMap.get('TOOL_TYPE_IMAGE_LAYERIZER')?.order ?? 26,
  }

  const aiPhotoStyles: AppSwitchBarItem = {
    toolType: ToolType.AI_PHOTO_STYLE,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AI_PHOTO_STYLES')?.cluster ?? 'image',
    active: true,
    showOnHomePage: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-photo-styles.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AI_PHOTO_STYLES,
    ...getColorClasses('bg-violet-500'),
    ariaLabel: 'Switch to AI Photo Styles',
    route: toolDefMap.get('TOOL_TYPE_AI_PHOTO_STYLES')?.route ?? ROUTES.AI_PHOTO_STYLES.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_AI_PHOTO_STYLES'),
    order: toolDefMap.get('TOOL_TYPE_AI_PHOTO_STYLES')?.order ?? 1.5,
  }

  const aiMockupGenerator: AppSwitchBarItem = {
    toolType: ToolType.AI_MOCKUP_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AI_MOCKUP_GENERATOR')?.cluster ?? 'image',
    active: true,
    showOnHomePage: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-mockup-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AI_MOCKUP_GENERATOR,
    ...getColorClasses('bg-violet-500'),
    ariaLabel: 'Switch to AI Mockup Generator',
    route:
      toolDefMap.get('TOOL_TYPE_AI_MOCKUP_GENERATOR')?.route ?? ROUTES.AI_MOCKUP_GENERATOR.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_AI_MOCKUP_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_AI_MOCKUP_GENERATOR')?.order ?? 1.6,
  }

  const vectorizer: AppSwitchBarItem = {
    toolType: ToolType.VECTORIZER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VECTORIZER')?.cluster ?? 'image',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/vectorizer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VECTORIZER,
    ...getColorClasses('bg-studio-tools-vectorizer'),
    linkClass: '[&>*]:h-[21px] [&>*]:min-w-[18px]',
    ariaLabel: 'Switch to Vectorizer',
    route: toolDefMap.get('TOOL_TYPE_VECTORIZER')?.route ?? ROUTES.VECTORIZER.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_VECTORIZER'),
    order: toolDefMap.get('TOOL_TYPE_VECTORIZER')?.order ?? 2,
  }

  const unlimitedVectorizer: AppSwitchBarItem = {
    ...vectorizer,
    toolType: ToolType.UNLIMITED_VECTORIZER,
    workspaceIds: ['unlimited'],
    ariaLabel: 'Switch to the Unlimited Vectorizer',
    route: ROUTES.UNLIMITED.VECTORIZER,
  }

  const aiSvgGenerator: AppSwitchBarItem = {
    toolType: ToolType.AISVG_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AISVG_GENERATOR')?.cluster ?? 'image',
    active: true,
    showOnHomePage: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-svg-generator-v2.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AISVG_GENERATOR,
    ...getColorClasses('bg-studio-tools-vectorizer'),
    ariaLabel: 'Switch to AI SVG Generator',
    route: toolDefMap.get('TOOL_TYPE_AISVG_GENERATOR')?.route ?? ROUTES.AI_SVG_GENERATOR.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_AISVG_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_AISVG_GENERATOR')?.order ?? 2.5,
  }

  const upscaler: AppSwitchBarItem = {
    toolType: ToolType.UPSCALER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_UPSCALER')?.cluster ?? 'image',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/upscaler.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_UPSCALER,
    ariaLabel: 'Switch to Upscaler',
    route: toolDefMap.get('TOOL_TYPE_UPSCALER')?.route ?? ROUTES.UPSCALER.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-upscaler'),
    ...getToolTranslations('TOOL_TYPE_UPSCALER'),
    order: toolDefMap.get('TOOL_TYPE_UPSCALER')?.order ?? 3,
  }

  const unlimitedUpscaler: AppSwitchBarItem = {
    ...upscaler,
    toolType: ToolType.UNLIMITED_UPSCALER,
    workspaceIds: ['unlimited'],
    ariaLabel: 'Switch to the Unlimited Upscaler',
    route: ROUTES.UNLIMITED.UPSCALER,
  }

  const backgroundRemover: AppSwitchBarItem = {
    toolType: ToolType.BG_REMOVER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_BG_REMOVER')?.cluster ?? 'image',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/background-remover.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_BG_REMOVER,
    ...getColorClasses('bg-clover-500'),
    linkClass: '[&>*]:h-[24px] [&>*]:min-w-[22px]',
    ariaLabel: 'Switch to Background Remover',
    route: toolDefMap.get('TOOL_TYPE_BG_REMOVER')?.route ?? ROUTES.BACKGROUND_REMOVER.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_BG_REMOVER'),
    order: toolDefMap.get('TOOL_TYPE_BG_REMOVER')?.order ?? 4,
  }

  const unlimitedBackgroundRemover: AppSwitchBarItem = {
    ...backgroundRemover,
    toolType: ToolType.UNLIMITED_BG_REMOVER,
    workspaceIds: ['unlimited'],
    ariaLabel: 'Switch to the Unlimited Background Remover',
    route: ROUTES.UNLIMITED.BACKGROUND_REMOVER,
  }

  const removePeopleFromBackground: AppSwitchBarItem = {
    toolType: ToolType.PEOPLE_REMOVER_FROM_BACKGROUND,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND')?.cluster ?? 'image',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/remove-people-from-background.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND,
    ...getColorClasses('bg-clover-500'),
    linkClass: '[&>*]:h-[24px] [&>*]:min-w-[22px]',
    ariaLabel: 'Switch to Remove People from Background',
    route:
      toolDefMap.get('TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND')?.route ??
      ROUTES.REMOVE_PEOPLE_FROM_BACKGROUND.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND'),
    order: toolDefMap.get('TOOL_TYPE_PEOPLE_REMOVER_FROM_BACKGROUND')?.order ?? 5,
  }

  const logoCreator: AppSwitchBarItem = {
    toolType: ToolType.LOGO_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_LOGO_GENERATOR')?.cluster ?? 'image',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/logo-creator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_LOGO_GENERATOR,
    ...getColorClasses('bg-studio-tools-logocreator'),
    ariaLabel: 'Switch to Logo Creator',
    route: toolDefMap.get('TOOL_TYPE_LOGO_GENERATOR')?.route ?? ROUTES.LOGO_CREATOR.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_LOGO_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_LOGO_GENERATOR')?.order ?? 6,
  }

  const aiQuoteGenerator: AppSwitchBarItem = {
    toolType: ToolType.QUOTE_GENERATOR,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_QUOTE_GENERATOR')?.cluster ?? 'crafting',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-quote-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_QUOTE_GENERATOR,
    ...getColorClasses('bg-studio-tools-logocreator'),
    ariaLabel: 'Switch to Quote Generator',
    route: toolDefMap.get('TOOL_TYPE_QUOTE_GENERATOR')?.route ?? ROUTES.QUOTE_GENERATOR.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_QUOTE_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_QUOTE_GENERATOR')?.order ?? 7,
  }

  const aiCollageMaker: AppSwitchBarItem = {
    toolType: ToolType.COLLAGE_MAKER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_COLLAGE_MAKER')?.cluster ?? 'crafting',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-collage-maker.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_COLLAGE_MAKER,
    ...getColorClasses('bg-studio-tools-logocreator'),
    ariaLabel: 'Switch to AI Collage Maker',
    route: toolDefMap.get('TOOL_TYPE_COLLAGE_MAKER')?.route ?? ROUTES.AI_COLLAGE_MAKER.ROOT,
    preload: true,
    ...getToolTranslations('TOOL_TYPE_COLLAGE_MAKER'),
    order: toolDefMap.get('TOOL_TYPE_COLLAGE_MAKER')?.order ?? 7.5,
  }

  const designReviewer: AppSwitchBarItem = {
    toolType: ToolType.DESIGN_REVIEWER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_DESIGN_REVIEWER')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/design-reviewer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_DESIGN_REVIEWER,
    ariaLabel: 'Switch to Design Reviewer',
    route: toolDefMap.get('TOOL_TYPE_DESIGN_REVIEWER')?.route ?? ROUTES.DESIGN_REVIEWER.ROOT,
    preload: true,
    ...getColorClasses('bg-yellow-500'),
    ...getToolTranslations('TOOL_TYPE_DESIGN_REVIEWER'),
    order: toolDefMap.get('TOOL_TYPE_DESIGN_REVIEWER')?.order ?? 8,
  }

  const fontGenerator: AppSwitchBarItem = {
    toolType: ToolType.FONT_GENERATOR,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_FONT_GENERATOR')?.cluster ?? 'crafting',
    active: false,
    image: <ToolImage src={`${LFS_IMAGES}/font-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_FONT_GENERATOR,
    linkClass: '[&>*]:h-[20px] [&>*]:min-w-[18px]',
    ariaLabel: 'Switch to Font Generator',
    route: toolDefMap.get('TOOL_TYPE_FONT_GENERATOR')?.route ?? ROUTES.FONT_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-fontgenerator'),
    ...getToolTranslations('TOOL_TYPE_FONT_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_FONT_GENERATOR')?.order ?? 9,
  }

  const aiMusicGenerator: AppSwitchBarItem = {
    toolType: ToolType.AI_MUSIC_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AI_MUSIC_GENERATOR')?.cluster ?? 'audio',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-music-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AI_MUSIC_GENERATOR,
    ariaLabel: 'Switch to Music Generator',
    route: toolDefMap.get('TOOL_TYPE_AI_MUSIC_GENERATOR')?.route ?? ROUTES.AI_MUSIC_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-musicgenerator'),
    ...getToolTranslations('TOOL_TYPE_AI_MUSIC_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_AI_MUSIC_GENERATOR')?.order ?? 9,
  }

  const aiVideoGenerator: AppSwitchBarItem = {
    toolType: ToolType.VIDEO_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_GENERATOR')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-video-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_GENERATOR,
    ariaLabel: 'Switch to Video Generator',
    route: toolDefMap.get('TOOL_TYPE_VIDEO_GENERATOR')?.route ?? ROUTES.AI_VIDEO_GENERATOR.ROOT,
    preload: true,
    showOnHomePage: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_GENERATOR')?.order ?? 9,
  }

  const draftMode: AppSwitchBarItem = {
    toolType: ToolType.VIDEO_DRAFT,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_DRAFT')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-video-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_DRAFT,
    ariaLabel: 'Switch to Draft Mode',
    route: toolDefMap.get('TOOL_TYPE_VIDEO_DRAFT')?.route ?? ROUTES.DRAFT_MODE.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_DRAFT'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_DRAFT')?.order ?? 9.05,
  }

  const unlimitedVideoGenerator: AppSwitchBarItem = {
    ...aiVideoGenerator,
    toolType: ToolType.UNLIMITED_VIDEO_GENERATOR,
    workspaceIds: ['unlimited'],
    // Production hides this Unlimited tool. The Studio video generator above stays.
    active: process.env.VITE_PUBLIC_ENV !== 'production',
    ariaLabel: 'Switch to the Unlimited Video Generator',
    route: ROUTES.UNLIMITED.VIDEO_GENERATOR.ROOT,
  }

  const videoUncensored: AppSwitchBarItem = {
    toolType: ToolType.VIDEO_UNCENSORED,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_UNCENSORED')?.cluster ?? 'video',
    active: Boolean(isNsfwPlatform),
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-video-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_UNCENSORED,
    ariaLabel: 'Switch to Video Uncensored',
    route: toolDefMap.get('TOOL_TYPE_VIDEO_UNCENSORED')?.route ?? ROUTES.AI_VIDEO_UNCENSORED.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_UNCENSORED'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_UNCENSORED')?.order ?? 9.1,
  }

  const aiVideoEditor: AppSwitchBarItem = {
    toolType: ToolType.VIDEO_EDITOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_EDITOR')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-video-editor.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_EDITOR,
    ariaLabel: 'Switch to AI Video Editor',
    route: toolDefMap.get('TOOL_TYPE_VIDEO_EDITOR')?.route ?? ROUTES.AI_VIDEO_EDITOR.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_EDITOR'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_EDITOR')?.order ?? 9.5,
  }

  const aiVideoUpscaler: AppSwitchBarItem = {
    toolType: ToolType.VIDEO_UPSCALER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_UPSCALER')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-video-upscaler-v2.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_UPSCALER,
    ariaLabel: 'Switch to AI Video Upscaler',
    route: toolDefMap.get('TOOL_TYPE_VIDEO_UPSCALER')?.route ?? ROUTES.AI_VIDEO_UPSCALER.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_UPSCALER'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_UPSCALER')?.order ?? 10,
  }

  const videoEditingToolbox: AppSwitchBarItem = {
    // No backend ToolType for the toolbox yet, so it follows the Workflows precedent: UNSPECIFIED
    // plus `hideFavorite`, which keeps the placeholder out of the favourites FavoriteTool persists.
    // Add TOOL_TYPE_VIDEO_EDITING_TOOLBOX to `creativefabrica/schemas` to make it pinnable.
    toolType: ToolType.UNSPECIFIED,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_VIDEO_EDITING_TOOLBOX')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    // Placeholder art: one of the tool card thumbnails until the toolbox gets its own.
    image: <ToolImage src={`${LFS_URL}/studio/video-editing-toolbox/extract-frames.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_VIDEO_EDITING_TOOLBOX,
    ariaLabel: 'Switch to Video Editing Toolbox',
    route:
      toolDefMap.get('TOOL_TYPE_VIDEO_EDITING_TOOLBOX')?.route ?? ROUTES.VIDEO_EDITING_TOOLBOX.ROOT,
    preload: true,
    hideFavorite: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_VIDEO_EDITING_TOOLBOX'),
    order: toolDefMap.get('TOOL_TYPE_VIDEO_EDITING_TOOLBOX')?.order ?? 12,
  }

  const imageAlive: AppSwitchBarItem = {
    toolType: ToolType.IMAGE_ALIVE,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_IMAGE_ALIVE')?.cluster ?? 'video',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/image-alive.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_IMAGE_ALIVE,
    ariaLabel: 'Switch to Image Alive',
    route: toolDefMap.get('TOOL_TYPE_IMAGE_ALIVE')?.route ?? ROUTES.IMAGE_ALIVE.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-videogenerator'),
    ...getToolTranslations('TOOL_TYPE_IMAGE_ALIVE'),
    order: toolDefMap.get('TOOL_TYPE_IMAGE_ALIVE')?.order ?? 11,
  }

  const aiSoundEffectGenerator: AppSwitchBarItem = {
    toolType: ToolType.SOUND_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_SOUND_GENERATOR')?.cluster ?? 'audio',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-sound-effect-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_SOUND_GENERATOR,
    ariaLabel: 'Switch to Sound Effect Generator',
    route:
      toolDefMap.get('TOOL_TYPE_SOUND_GENERATOR')?.route ?? ROUTES.AI_SOUND_EFFECT_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-soundeffectgenerator'),
    ...getToolTranslations('TOOL_TYPE_SOUND_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_SOUND_GENERATOR')?.order ?? 10,
  }

  const aiSpeechGenerator: AppSwitchBarItem = {
    toolType: ToolType.SPEECH_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_SPEECH_GENERATOR')?.cluster ?? 'audio',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-text-to-speech.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_SPEECH_GENERATOR,
    ariaLabel: 'Switch to Speech Generator',
    route: speechGeneratorRoute(
      activeWorkspaceId,
      toolDefMap.get('TOOL_TYPE_SPEECH_GENERATOR')?.route,
    ),
    preload: true,
    ...getColorClasses('bg-studio-tools-speechgenerator'),
    ...getToolTranslations('TOOL_TYPE_SPEECH_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_SPEECH_GENERATOR')?.order ?? 11,
  }

  const watermarkRemover: AppSwitchBarItem = {
    toolType: ToolType.AI_WATERMARK_REMOVER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AI_WATERMARK_REMOVER')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-watermark-remover.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AI_WATERMARK_REMOVER,
    ariaLabel: 'Switch to Watermark Remover',
    route:
      toolDefMap.get('TOOL_TYPE_AI_WATERMARK_REMOVER')?.route ?? ROUTES.AI_WATERMARK_REMOVER.ROOT,
    preload: true,
    ...getColorClasses('bg-clover-500'),
    ...getToolTranslations('TOOL_TYPE_AI_WATERMARK_REMOVER'),
    order: toolDefMap.get('TOOL_TYPE_AI_WATERMARK_REMOVER')?.order ?? 12,
  }

  const fileConverter: AppSwitchBarItem = {
    toolType: ToolType.FILE_CONVERTER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_FILE_CONVERTER')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/file-converter.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_FILE_CONVERTER,
    ariaLabel: 'Switch to File Converter',
    route: toolDefMap.get('TOOL_TYPE_FILE_CONVERTER')?.route ?? ROUTES.FILE_CONVERTER.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-fileconverter'),
    ...getToolTranslations('TOOL_TYPE_FILE_CONVERTER'),
    order: toolDefMap.get('TOOL_TYPE_FILE_CONVERTER')?.order ?? 12,
  }

  const imageCompressor: AppSwitchBarItem = {
    toolType: ToolType.IMAGE_COMPRESSOR,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_IMAGE_COMPRESSOR')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/image-compressor.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_IMAGE_COMPRESSOR,
    ariaLabel: 'Switch to Image Compressor',
    route: toolDefMap.get('TOOL_TYPE_IMAGE_COMPRESSOR')?.route ?? ROUTES.IMAGE_COMPRESSOR.ROOT,
    preload: true,
    ...getColorClasses('bg-studzio-tools-imagecompressor'),
    ...getToolTranslations('TOOL_TYPE_IMAGE_COMPRESSOR'),
    order: toolDefMap.get('TOOL_TYPE_IMAGE_COMPRESSOR')?.order ?? 13,
  }

  const transfer: AppSwitchBarItem = {
    toolType: ToolType.TRANSFERS,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_TRANSFERS')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/transfer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_TRANSFERS,
    ariaLabel: 'Switch to Transfer',
    route: toolDefMap.get('TOOL_TYPE_TRANSFERS')?.route ?? ROUTES.TRANSFER.ROOT,
    preload: true,
    ...getColorClasses('bg-studio-tools-transfer'),
    ...getToolTranslations('TOOL_TYPE_TRANSFERS'),
    order: toolDefMap.get('TOOL_TYPE_TRANSFERS')?.order ?? 14,
  }

  const studio: AppSwitchBarItem = {
    toolType: ToolType.STUDIO_DESIGNER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_STUDIO_DESIGNER')?.cluster ?? 'crafting',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/studio.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_STUDIO_DESIGNER,
    linkClass: '[&>*]:h-[24px] [&>*]:min-w-[24px]',
    ariaLabel: 'Switch to Studio',
    route: toolDefMap.get('TOOL_TYPE_STUDIO_DESIGNER')?.route ?? ROUTES.STUDIO.NEW_PROJECT,
    // do not preload here otherwise it can create a new project in the background
    preload: false,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_STUDIO_DESIGNER'),
    order: toolDefMap.get('TOOL_TYPE_STUDIO_DESIGNER')?.order ?? 22,
  }

  const lumo: AppSwitchBarItem = {
    toolType: ToolType.LUMO,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_LUMO')?.cluster ?? 'workspaces',
    active: true,
    image: <ToolImage src={`${LFS_IMAGES}/lumo_v1.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_LUMO,
    ariaLabel: 'Switch to Lumo',
    route: toolDefMap.get('TOOL_TYPE_LUMO')?.route ?? ROUTES.LUMO.ROOT,
    preload: false,
    hardNavigation: true,
    ...getColorClasses('bg-purple-500'),
    ...getToolTranslations('TOOL_TYPE_LUMO'),
    order: toolDefMap.get('TOOL_TYPE_LUMO')?.order ?? 15,
  }

  const threedModelViewer: AppSwitchBarItem = {
    toolType: ToolType.THREED_MODEL_VIEWER,
    category: 'ai',
    active: true,
    cluster: toolDefMap.get('TOOL_TYPE_THREED_MODEL_VIEWER')?.cluster ?? '3d',
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/3d-model-viewer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_THREED_MODEL_VIEWER,
    ariaLabel: 'Switch to 3D Model Viewer',
    route:
      toolDefMap.get('TOOL_TYPE_THREED_MODEL_VIEWER')?.route ?? ROUTES.THREED_MODEL_VIEWER.ROOT,
    preload: true,
    ...getColorClasses('bg-emerald-500'),
    ...getToolTranslations('TOOL_TYPE_THREED_MODEL_VIEWER'),
    order: toolDefMap.get('TOOL_TYPE_THREED_MODEL_VIEWER')?.order ?? 16,
  }

  const threedSculpt: AppSwitchBarItem = {
    toolType: ToolType.THREE_D_SCULPT,
    category: 'ai',
    active: true,
    cluster: toolDefMap.get('TOOL_TYPE_THREED_SCULPT')?.cluster ?? '3d',
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/3d-sculpting.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_THREED_SCULPT,
    ariaLabel: 'Switch to 3D Sculpt',
    route: toolDefMap.get('TOOL_TYPE_THREED_SCULPT')?.route ?? ROUTES.THREED_SCULPT.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_THREED_SCULPT'),
    order: toolDefMap.get('TOOL_TYPE_THREED_SCULPT')?.order ?? 24,
  }

  const worldCreator: AppSwitchBarItem = {
    toolType: ToolType.SPLAT_GENERATOR,
    category: 'ai',
    active: true,
    cluster: toolDefMap.get('TOOL_TYPE_SPLAT_GENERATOR')?.cluster ?? '3d',
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/world-creator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_SPLAT_GENERATOR,
    ariaLabel: 'Switch to World Creator',
    route: toolDefMap.get('TOOL_TYPE_SPLAT_GENERATOR')?.route ?? ROUTES.WORLD_CREATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-teal-500'),
    ...getToolTranslations('TOOL_TYPE_SPLAT_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_SPLAT_GENERATOR')?.order ?? 25,
  }

  const imageResizer: AppSwitchBarItem = {
    toolType: ToolType.IMAGE_RESIZER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_IMAGE_RESIZER')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: false,
    image: <ToolImage src={`${LFS_IMAGES}/image-resizer.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_IMAGE_RESIZER,
    ariaLabel: 'Switch to Image Resizer',
    route: toolDefMap.get('TOOL_TYPE_IMAGE_RESIZER')?.route ?? ROUTES.IMAGE_RESIZER.ROOT,
    preload: true,
    ...getColorClasses('bg-green-500'),
    ...getToolTranslations('TOOL_TYPE_IMAGE_RESIZER'),
    order: toolDefMap.get('TOOL_TYPE_IMAGE_RESIZER')?.order ?? 17,
  }

  const threedPrintGenerator: AppSwitchBarItem = {
    toolType: ToolType.THREE_D_MODEL_GENERATOR,
    category: 'ai',
    active: true,
    cluster: toolDefMap.get('TOOL_TYPE_THREED_PRINT_GENERATOR')?.cluster ?? '3d',
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/3d-print-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_THREED_PRINT_GENERATOR,
    ariaLabel: 'Switch to 3D Print Generator',
    route:
      toolDefMap.get('TOOL_TYPE_THREED_PRINT_GENERATOR')?.route ??
      ROUTES.THREED_PRINT_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_THREED_PRINT_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_THREED_PRINT_GENERATOR')?.order ?? 18,
  }

  const meshGenerator: AppSwitchBarItem = {
    toolType: ToolType.MESH_GENERATOR,
    category: 'ai',
    active: true,
    cluster: toolDefMap.get('TOOL_TYPE_MESH_GENERATOR')?.cluster ?? '3d',
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/3d-model-generator-v2.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_MESH_GENERATOR,
    ariaLabel: 'Switch to 3D Model Generator',
    route: toolDefMap.get('TOOL_TYPE_MESH_GENERATOR')?.route ?? ROUTES.THREED_MODEL_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_MESH_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_MESH_GENERATOR')?.order ?? 19,
  }

  const doodlePetGenerator: AppSwitchBarItem = {
    toolType: ToolType.DOODLE_PET,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_DOODLE_PET_GENERATOR')?.cluster ?? 'entertainment',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/doodle-pet-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_DOODLE_PET_GENERATOR,
    ariaLabel: 'Switch to Doodle Pet Generator',
    route:
      toolDefMap.get('TOOL_TYPE_DOODLE_PET_GENERATOR')?.route ?? ROUTES.DOODLE_PET_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-yellow-500'),
    ...getToolTranslations('TOOL_TYPE_DOODLE_PET_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_DOODLE_PET_GENERATOR')?.order ?? 18,
  }

  const colorPalettePicker: AppSwitchBarItem = {
    toolType: ToolType.COLOR_PALETTE_PICKER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_COLOR_PALETTE_PICKER')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/color-palette-picker.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_COLOR_PALETTE_PICKER,
    ariaLabel: 'Switch to Color Palette Picker',
    route:
      toolDefMap.get('TOOL_TYPE_COLOR_PALETTE_PICKER')?.route ?? ROUTES.COLOR_PALETTE_PICKER.ROOT,
    preload: false,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_COLOR_PALETTE_PICKER'),
    order: toolDefMap.get('TOOL_TYPE_COLOR_PALETTE_PICKER')?.order ?? 19,
  }

  const coloringPageMaker: AppSwitchBarItem = {
    toolType: ToolType.COLORING_PAGE_MAKER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_AI_COLORING_PAGE_MAKER')?.cluster ?? 'entertainment',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-coloring-page-maker.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AI_COLORING_PAGE_MAKER,
    ariaLabel: 'Switch to AI Coloring Page Maker',
    route:
      toolDefMap.get('TOOL_TYPE_AI_COLORING_PAGE_MAKER')?.route ??
      ROUTES.AI_COLORING_PAGE_MAKER.ROOT,
    preload: true,
    ...getColorClasses('bg-green-500'),
    ...getToolTranslations('TOOL_TYPE_AI_COLORING_PAGE_MAKER'),
    order: toolDefMap.get('TOOL_TYPE_AI_COLORING_PAGE_MAKER')?.order ?? 20,
  }

  const weddingInviteGenerator: AppSwitchBarItem = {
    toolType: ToolType.WEDDING_INVITE_GENERATOR,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_WEDDING_INVITE_GENERATOR')?.cluster ?? 'crafting',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/ai-wedding-invite-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_WEDDING_INVITE_GENERATOR,
    ariaLabel: 'Switch to Wedding Invite Generator',
    route:
      toolDefMap.get('TOOL_TYPE_WEDDING_INVITE_GENERATOR')?.route ??
      ROUTES.WEDDING_INVITE_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-pink-500'),
    ...getToolTranslations('TOOL_TYPE_WEDDING_INVITE_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_WEDDING_INVITE_GENERATOR')?.order ?? 21,
  }

  const etsyCoach: AppSwitchBarItem = {
    toolType: ToolType.ETSY_COACH,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_ETSY_COACH')?.cluster ?? 'utilities',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/etsycoach.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_ETSY_COACH,
    ariaLabel: 'Switch to Etsy Coach',
    route: toolDefMap.get('TOOL_TYPE_ETSY_COACH')?.route ?? ROUTES.ETSY_COACH.ROOT,
    preload: true,
    ...getColorClasses('bg-orange-500'),
    ...getToolTranslations('TOOL_TYPE_ETSY_COACH'),
    order: toolDefMap.get('TOOL_TYPE_ETSY_COACH')?.order ?? 22,
  }

  const toonFamilyBuilder: AppSwitchBarItem = {
    toolType: ToolType.TOON_FAMILY_BUILDER,
    category: 'more',
    cluster: toolDefMap.get('TOOL_TYPE_TOON_FAMILY_BUILDER')?.cluster ?? 'entertainment',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/toon-family-builder.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_TOON_FAMILY_BUILDER,
    ariaLabel: 'Switch to ToonFamily Builder',
    route:
      toolDefMap.get('TOOL_TYPE_TOON_FAMILY_BUILDER')?.route ?? ROUTES.TOON_FAMILY_BUILDER.ROOT,
    preload: true,
    ...getColorClasses('bg-purple-500'),
    ...getToolTranslations('TOOL_TYPE_TOON_FAMILY_BUILDER'),
    order: toolDefMap.get('TOOL_TYPE_TOON_FAMILY_BUILDER')?.order ?? 23,
  }

  const cameraAngle: AppSwitchBarItem = {
    toolType: ToolType.CAMERA_ANGLE,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_CAMERA_ANGLE')?.cluster ?? 'image',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/camera-angle-control.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_CAMERA_ANGLE,
    ariaLabel: 'Switch to Camera Angle',
    route: toolDefMap.get('TOOL_TYPE_CAMERA_ANGLE')?.route ?? ROUTES.CAMERA_ANGLE.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_CAMERA_ANGLE'),
    order: toolDefMap.get('TOOL_TYPE_CAMERA_ANGLE')?.order ?? 25,
  }

  const adCampaignGenerator: AppSwitchBarItem = {
    toolType: ToolType.AD_CAMPAIGNS,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_AD_CAMPAIGN_GENERATOR')?.cluster ?? 'marketing',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/ad-campaign-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_AD_CAMPAIGN_GENERATOR,
    ariaLabel: 'Switch to Ad Campaign Generator',
    route:
      toolDefMap.get('TOOL_TYPE_AD_CAMPAIGN_GENERATOR')?.route ?? ROUTES.AD_CAMPAIGN_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_AD_CAMPAIGN_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_AD_CAMPAIGN_GENERATOR')?.order ?? 1,
  }

  const subtitleGenerator: AppSwitchBarItem = {
    toolType: ToolType.SUBTITLE_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_SUBTITLE_GENERATOR')?.cluster ?? 'marketing',
    active: true,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/subtitle-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_SUBTITLE_GENERATOR,
    ariaLabel: 'Switch to Subtitle Generator',
    route: toolDefMap.get('TOOL_TYPE_SUBTITLE_GENERATOR')?.route ?? ROUTES.SUBTITLE_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_SUBTITLE_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_SUBTITLE_GENERATOR')?.order ?? 2,
  }

  const productManager: AppSwitchBarItem = {
    toolType: ToolType.PRODUCT_MANAGER,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_PRODUCT_MANAGER')?.cluster ?? 'marketing',
    active: true,
    showInEllipsisMenu: true,
    image: <ToolImage src={`${LFS_IMAGES}/product-manager.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_PRODUCT_MANAGER,
    ariaLabel: 'Switch to Product Manager',
    route: toolDefMap.get('TOOL_TYPE_PRODUCT_MANAGER')?.route ?? ROUTES.PRODUCT_MANAGER.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_PRODUCT_MANAGER'),
    order: toolDefMap.get('TOOL_TYPE_PRODUCT_MANAGER')?.order ?? 3,
  }

  const youtubeThumbnailGenerator: AppSwitchBarItem = {
    toolType: ToolType.YOUTUBE_THUMBNAIL_GENERATOR,
    category: 'ai',
    cluster: toolDefMap.get('TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR')?.cluster ?? 'marketing',
    active: isYoutubeThumbnailGeneratorEnabled,
    showInEllipsisMenu: true,
    showOnHomePage: true,
    image: <ToolImage src={`${LFS_IMAGES}/youtube-thumbnail-generator.webp`} />,
    icon: TOOL_ICONS.TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR,
    ariaLabel: 'Switch to YouTube Thumbnail Generator',
    route:
      toolDefMap.get('TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR')?.route ??
      ROUTES.YOUTUBE_THUMBNAIL_GENERATOR.ROOT,
    preload: true,
    ...getColorClasses('bg-violet-500'),
    ...getToolTranslations('TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR'),
    order: toolDefMap.get('TOOL_TYPE_YOUTUBE_THUMBNAIL_GENERATOR')?.order ?? 5,
  }

  const items = [
    vectorizer,
    aiSvgGenerator,
    upscaler,
    backgroundRemover,
    removePeopleFromBackground,
    flow,
    unlimitedImageGenerator,
    unlimitedBackgroundRemover,
    unlimitedUpscaler,
    unlimitedVectorizer,
    unlimitedVideoGenerator,
    imageLayerizer,
    aiImageEditor,
    aiQuoteGenerator,
    aiCollageMaker,
    logoCreator,
    designReviewer,
    fontGenerator,
    aiMusicGenerator,
    aiVideoEditor,
    aiVideoGenerator,
    draftMode,
    videoUncensored,
    aiVideoUpscaler,
    videoEditingToolbox,
    imageAlive,
    aiSoundEffectGenerator,
    aiSpeechGenerator,
    watermarkRemover,
    fileConverter,
    imageCompressor,
    transfer,
    studio,
    lumo,
    threedModelViewer,
    imageResizer,
    aiPhotoStyles,
    aiMockupGenerator,
    threedPrintGenerator,
    meshGenerator,
    doodlePetGenerator,
    colorPalettePicker,
    coloringPageMaker,
    weddingInviteGenerator,
    etsyCoach,
    toonFamilyBuilder,
    threedSculpt,
    worldCreator,
    cameraAngle,
    adCampaignGenerator,
    subtitleGenerator,
    productManager,
    youtubeThumbnailGenerator,
  ].filter(
    (menu): menu is AppSwitchBarItem =>
      menu.active && isOfferedInWorkspace(menu, activeWorkspaceId),
  )

  // Desktop policy hook: currently a pass-through (all tools shown on desktop).
  return filterDesktopTools(items, isStudioDesktop())
}
interface AppSwitchBarProps {
  showEllipsisPopoverMenu?: boolean
}

export function AppSwitchBar({ showEllipsisPopoverMenu = true }: AppSwitchBarProps) {
  const appSwitchItems = useAppSwitchItems()
  const visibleMenus = appSwitchItems.filter(m => !m.showInEllipsisMenu)
  const ellipsisMenus = appSwitchItems.filter(m => m.showInEllipsisMenu)

  return (
    <div className="flex flex-row gap-1">
      {visibleMenus.map(menu => (
        <AppButton key={menu.toolType} menu={menu} />
      ))}
      {showEllipsisPopoverMenu ? (
        <div className="size-10">
          <Popover.Root>
            <EllipsisMenu ellipsisMenus={ellipsisMenus} />
          </Popover.Root>
        </div>
      ) : null}
    </div>
  )
}

const AUTO_CLOSE_DISTANCE_PIXELS = 50
const THROTTLE_TIME_MS = 100

function EllipsisMenu({ ellipsisMenus }: { ellipsisMenus: AppSwitchBarItem[] }) {
  const popover = Popover.usePopoverContext()
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!popover.open) {
      return
    }
    const id = setTimeout(() => popover.setOpen(false), 5000)
    return () => clearTimeout(id)
  }, [popover.open, popover])

  const throttledHandler = React.useMemo(() => {
    return throttle((e: MouseEvent) => {
      const menuEl = document.querySelector('[data-ellipsis-menu="true"]') as HTMLElement | null
      if (!menuEl) {
        return
      }

      if (
        distanceToRect(e.clientX, e.clientY, menuEl.getBoundingClientRect()) >
        AUTO_CLOSE_DISTANCE_PIXELS
      ) {
        popover.setOpen(false)
      }
    }, THROTTLE_TIME_MS)
  }, [popover])

  const mouseMoveListener = React.useCallback(
    (e: Event) => {
      throttledHandler(e as MouseEvent)
    },
    [throttledHandler],
  )

  useWindowDomEvent('mousemove', popover.open ? mouseMoveListener : false)

  return (
    <>
      <Popover.Trigger asChild>
        <Button
          iconOnly
          aria-label="More options"
          ref={triggerRef}
          onMouseOver={() => popover.setOpen(true)}
          onClick={() => popover.setOpen(!popover.open)}
          className="text-gray-400-400 bg-gray-1000-100"
          variant="secondary"
        >
          <DotsVerticalIcon />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content data-ellipsis-menu="true" side="bottom" align="start" alignOffset={-10}>
          <Scroller asChild>
            <PopoverComponentsScrollable className="flex flex-col gap-1">
              {ellipsisMenus.map(menu => (
                <AppButton key={menu.toolType} menu={menu} side="right" align="start" />
              ))}
            </PopoverComponentsScrollable>
          </Scroller>
        </Popover.Content>
      </Popover.Portal>
    </>
  )
}

function AppButton({
  menu,
  side = 'bottom',
  align = 'end',
}: {
  menu: AppSwitchBarItem
  side?: 'top' | 'right' | 'bottom' | 'left'
  align?: 'start' | 'center' | 'end'
}) {
  const pathname = useStudioPathname()
  return (
    <Tooltip
      key={menu.toolType}
      value={
        <>
          <div
            className={tw(
              'flex w-full flex-grow flex-col justify-between gap-4 px-2 py-1',
              menu.title === 'Background Remover' && 'px-1',
            )}
          >
            <div className="rounded-2 flex flex-grow overflow-hidden">{menu.image}</div>
            <div className="flex flex-row items-start gap-4">
              <div
                className={tw(
                  'rounded-2 text-service-white-white flex h-[48px] min-w-[48px] items-center justify-center [&>*]:h-[24px] [&>*]:min-w-[24px]',
                  menu.colorClass,
                )}
              >
                {menu.icon}
              </div>
              <div className="flex flex-col gap-1">
                <p className="typography-16 flex items-center gap-2 text-start text-gray-100">
                  {menu.title}
                </p>
                <p className="text-gray-400-400 typography-14 flex text-start">
                  {menu.description.default}
                </p>
              </div>
            </div>
          </div>
        </>
      }
      side={side}
      sideOffset={9}
      align={align}
      className="bg-gray-1000 rounded-4 z-[100] flex max-h-[400px] w-[370px] flex-col justify-between shadow-md"
      arrowClassName="fill-gray-1000"
    >
      <PreloadedLink
        preload={menu.preload}
        to={menu.route}
        aria-label={menu.ariaLabel}
        className={tw(
          'bg-gray-1000-100 rounded-2 text-gray-400-400 flex cursor-pointer items-center justify-center border-0 p-[5px] focus:outline-none',
          'hover:text-service-white-white focus:text-service-white-white h-10 w-10',
          menu.hoverColorClass,
          menu.focusColorClass,
          `[&>*]:h-[20px] [&>*]:w-[18px] ${menu.linkClass}`,
          pathname.startsWith(menu.route) && `text-service-white-white ${menu.colorClass}`,
        )}
      >
        {menu.icon}
      </PreloadedLink>
    </Tooltip>
  )
}

function ToolImage({ src, className }: { src: string; className?: string }) {
  const t = useStudioTranslate()
  return (
    <img
      src={src}
      width="615"
      height="380"
      className={className || 'bg-gray-800 object-cover aspect-[615/380]'}
      alt={t('studio.common.studio_app')}
    />
  )
}

function getColorClasses(colorClass: string) {
  return {
    colorClass,
    hoverColorClass: `hover:${colorClass}`,
    focusColorClass: `focus:${colorClass}`,
  }
}
